import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import type {Context} from '../types';

/**
 * This Patch encodes email subject of the Link Node if needed and updates data.attr.href for not dynbound links.
 * It also decodes manually encoded email subjects.
 */

// List of allowed characters can be found here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
const urlCharactersRegex = /[^a-zA-Z0-9_\-.!~*'()]+/;

// Inline util to check types of links from when this patch was introduced
const isLinkEmailChecker = function (data: any): boolean {
  return Boolean(data && data.link && data.link.mode === 'email');
};

export default (context: Context) => {
  const {
    node: {jsonNode},
  } = context;
  const {data = {}} = jsonNode;
  const isDynBound = DomTypeUtils.isDynBound(data);
  const isLinkEmail = isLinkEmailChecker(data);

  let patchNeeded = false;
  let subject;
  let encodedSubject;
  let decodedSubject: string | undefined;
  let hrefSubjectEncodingNeeded = false;
  let subjectDecodingNeeded = false;
  let hrefWithEncoding: string;

  if (
    isLinkEmail &&
    data.link.subject &&
    urlCharactersRegex.test(data.link.subject)
  ) {
    subject = data.link.subject;
    try {
      decodedSubject = decodeURIComponent(subject);
    } catch (ex: any) {
      console.log('ERROR DECODING URI: ' + ex.message);
    }

    // Do we need to decode the subject
    if (decodedSubject) {
      subjectDecodingNeeded = Boolean(
        decodedSubject && subject !== decodedSubject
      );
    }

    if (!subjectDecodingNeeded) {
      try {
        encodedSubject = encodeURIComponent(subject);
      } catch (ex: any) {
        console.log('ERROR ENCODING URI: ' + ex.message);
        encodedSubject = subject;
      }

      hrefWithEncoding =
        'mailto:' + data.link.email + '?subject=' + encodedSubject;

      // Do we need to update/encode the href attr
      hrefSubjectEncodingNeeded =
        !isDynBound && data.attr && data.attr.href !== hrefWithEncoding;
    }

    patchNeeded = subjectDecodingNeeded || hrefSubjectEncodingNeeded;
  }

  return {
    needed: patchNeeded,
    logData: {
      msg: 'Email Link with unencoded subject',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      if (subjectDecodingNeeded) {
        data.link.subject = decodedSubject;
      } else {
        data.attr.href = hrefWithEncoding;
      }
    },
  };
};
